import React, { useState, useEffect, useMemo } from 'react'
import { Checkbox } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { PageHeader, LoaderBar, AppEditButton, PlanUsageStrip } from 'components'
import styled from 'styled-components'
import { GetMenuList } from 'hooks/menu-setting'
import { GetAuditCompanyList } from 'hooks'
import { PermissionSettingService } from 'utility/services/permission'
import { MenuSettingService } from 'utility/services/menu-setting'
import { UsersService } from 'utility/services/user'
import { useNotification } from 'hooks/toast'
import { useAuth } from 'context'
import { useSubscriptionUsage } from 'context/subscription-usage'
import { LoginAsUserModal } from 'page-components/settings/tabs/impersonation/LoginAsUserModal'
import { CreateUserModal } from './CreateUserModal'
import { UserEditModal } from 'page-components/settings/tabs/user/UserEditModal'
import { AppPrimaryButton } from 'elements'
import {
    Scroll,
    GearSix,
    Users,
    UsersThree,
    ListChecks,
    ClipboardText,
    Ticket,
    FileText,
    Package,
    Key,
    SquaresFour,
    CaretDown,
    CaretRight,
    UserSwitch,
} from 'phosphor-react'

const Container = styled.div`
    padding: 10px 0;
`

const SectionCard = styled.div`
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background-color: #fff;
    margin-bottom: 24px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`

const CardHeader = styled.div`
    background-color: #f8f9fa;
    padding: 12px 18px;
    font-weight: 600;
    font-size: 15px;
    color: #374151;
    border-bottom: 1px solid #e5e7eb;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
`

const CardBody = styled.div`
    padding: 18px;
`

const DropdownGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
`

const ModuleRowContainer = styled.div`
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    margin-bottom: 16px;
    overflow: hidden;
`

const ModuleHeaderRow = styled.div`
    padding: 14px 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: #fff;
    cursor: pointer;
    transition: background-color 0.2s ease;
    &:hover {
        background-color: #f9fafb;
    }
`

const IconContainer = styled.div`
    width: 42px;
    height: 42px;
    background-color: #e3f2fd;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: bold;
    color: #1565c0;
    margin-right: 16px;
    flex-shrink: 0;
`

const SubRow = styled.div`
    padding: 12px 18px;
    background-color: #fff;
    border-bottom: 1px solid #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: space-between;
    &:last-child {
        border-bottom: none;
    }
`

const SubHeader = styled.div`
    background-color: #f8f9fa;
    padding: 8px 18px;
    font-size: 11px;
    font-weight: 600;
    color: #4b5563;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #e5e7eb;
`

const ImpersonateStrip = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
    padding: 12px 16px;
    background: #f0fdf4;
    border: 1px dashed #86efac;
    border-radius: 8px;
    .label {
        flex: 1;
        font-size: 12.5px;
        color: #166534;
    }
`

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;

    > *:first-child {
        flex: 1 1 auto;
        min-width: 0;
        margin-bottom: 0 !important;
    }
`

const UserDetailCard = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;
    padding: 14px 16px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`

const UserDetailGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 10px 20px;
    flex: 1;
    min-width: 0;
`

const DetailItem = styled.div`
    .label {
        font-size: 11px;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        margin-bottom: 2px;
    }
    .value {
        font-size: 13.5px;
        color: #111827;
        font-weight: 500;
        word-break: break-word;
    }
`

const StatusDot = styled.span`
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 6px;
    background: ${({ active }) => (active ? '#16a34a' : '#9ca3af')};
`

const getMenuIcon = (iconKey, title, size = 22) => {
    if (title === 'Invoice' || title === 'Invoice Settings') {
        return <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#1565c0' }}>$</span>
    }
    if (title === 'Audit' || title === 'Report') {
        return <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#1565c0' }}>A</span>
    }
    const ICON_MAP = {
        templates: Scroll,
        report: GearSix,
        'report-item': GearSix,
        customer: Users,
        group: UsersThree,
        task: ListChecks,
        'action-plan': ClipboardText,
        settings: GearSix,
        support: Ticket,
        invoice: FileText,
        'invoice-item': FileText,
        asset: Package,
        key: Key,
    }
    const IconComponent = ICON_MAP[iconKey] || SquaresFour
    return <IconComponent size={size} weight="bold" />
}

const MODULE_DESCRIPTIONS = {
    'menu.invoice': 'Generate invoices, view history, audit completions.',
    'menu.report': 'Daily inspections, scoring, NC reports.',
    'menu.dashboard': 'Operational charts. Single-page module.',
    'menu.templates': 'Manage document and checklist templates.',
    'menu.customer': 'Manage customers and clients.',
    'menu.group': 'Manage user groups and organizational units.',
    'menu.task': 'Track and assign tasks.',
    'menu.settings': 'General system settings and configurations.',
    'menu.support': 'Support tickets and assistance.',
}

const getMenuPermissionKeys = menu => {
    const menuKey = menu?.MenuKey || ''
    const keysToCheck = new Set([
        `${menuKey}.view`.toLowerCase(),
        menuKey.toLowerCase(),
    ])
    if (menuKey.toLowerCase().startsWith('menu.')) {
        const stripped = menuKey.slice(5)
        keysToCheck.add(`${stripped}.view`.toLowerCase())
        keysToCheck.add(stripped.toLowerCase())
    }
    return keysToCheck
}

const hasMenuPermission = (permissions, menu) => {
    const keysToCheck = getMenuPermissionKeys(menu)
    return permissions.some(p => p.PermissionKey && keysToCheck.has(p.PermissionKey.toLowerCase()))
}

// Module-level cache to store user options and prevent multiple API calls when the tab remounts
let cachedUserOptions = null

export const UserMenuPermissionScreen = () => {
    const { user: currentUser } = useAuth()
    const { refetchUsage, isFreePlan } = useSubscriptionUsage()
    const { successToast, errorToast } = useNotification()
    const [selectedUserId, setSelectedUserId] = useState(null)

    const [userOptions, setUserOptions] = useState([])
    const [loadingUsers, setLoadingUsers] = useState(false)

    const [userPermissions, setUserPermissions] = useState([])
    const [loadingPermissions, setLoadingPermissions] = useState(false)

    const [companyPermissions, setCompanyPermissions] = useState([])
    const [loadingCompanyPermissions, setLoadingCompanyPermissions] = useState(false)

    const [expandedModules, setExpandedModules] = useState({})
    const [modulePermissionsMap, setModulePermissionsMap] = useState({})
    const [loadingModuleId, setLoadingModuleId] = useState(null)

    const [impersonateModalOpen, setImpersonateModalOpen] = useState(false)
    const [createUserModalOpen, setCreateUserModalOpen] = useState(false)
    const [editUserModalOpen, setEditUserModalOpen] = useState(false)

    const menuParams = useMemo(() => ({ onlyActive: true, onlyVisible: true }), [])
    const { data: menuList, loading: loadingMenuList } = GetMenuList(menuParams)

    const isSuperAdmin = currentUser?.IsSuperAdmin
    const canAddUser =
        currentUser?.IsSuperAdmin === true ||
        currentUser?.IsAdmin === true ||
        currentUser?.RoleId === 3 ||
        currentUser?.RoleId === 1000
    const [selectedCompanyId, setSelectedCompanyId] = useState(null)
    const [companySearchText, setCompanySearchText] = useState('')

    const {
        data: companies,
        loading: loadingCompanies,
        filterChanged: companyFilterChanged,
    } = GetAuditCompanyList(isSuperAdmin, true, 50)

    const companyOptions = useMemo(() => {
        if (!companies) return []
        return companies.map(ele => ({
            key: ele.CompanyId,
            value: ele.CompanyId,
            text: `${ele.CompanyName || ele.Name} (CompanyId ${ele.CompanyId})`,
        }))
    }, [companies])

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (isSuperAdmin) {
                companyFilterChanged({ search: companySearchText })
            }
        }, 500)

        return () => clearTimeout(delayDebounceFn)
    }, [companySearchText, isSuperAdmin, companyFilterChanged])

    const callerCompanyId = useMemo(() => {
        return currentUser?.CompanyId || currentUser?.Companies?.[0]?.CompanyId
    }, [currentUser])

    const targetCompanyId = isSuperAdmin ? selectedCompanyId : callerCompanyId

    // Reset selected user when company changes
    useEffect(() => {
        setSelectedUserId(null)
    }, [targetCompanyId])

    // Load company's available permissions
    useEffect(() => {
        const fetchCompanyPermissions = async () => {
            if (!targetCompanyId) {
                setCompanyPermissions([])
                return
            }
            setLoadingCompanyPermissions(true)
            try {
                const res = await PermissionSettingService.getCompanyPermissions(targetCompanyId)
                const perms = Array.isArray(res) ? res : (res?.data || [])
                setCompanyPermissions(perms)
            } catch (error) {
                console.error('Error loading company permissions:', error)
                errorToast('Failed to load company permissions.')
            } finally {
                setLoadingCompanyPermissions(false)
            }
        }
        fetchCompanyPermissions()
    }, [targetCompanyId])

    const fetchUsers = async (force = false) => {
        if (isSuperAdmin && !selectedCompanyId) {
            setUserOptions([])
            return
        }
        if (!isSuperAdmin && cachedUserOptions && !force) {
            setUserOptions(cachedUserOptions)
            return
        }
        setLoadingUsers(true)
        try {
            const queryParams = {
                PageNumber: 1,
                PageSize: 5000000,
                SearchText: '',
                all: 'All',
            }
            if (targetCompanyId) {
                queryParams.CompanyId = targetCompanyId
            }
            const res = await UsersService.getTeamUsers(queryParams)
            const items = Array.isArray(res) ? res : (res?.Items || [])
            const options = items.map(u => ({
                key: u.unique_id,
                value: u.unique_id,
                text: `${u.Name || u.FullName || u.Email} (${u.Email})`,
                name: u.Name || u.FullName,
                email: u.Email,
                user: u,
            }))
            if (!isSuperAdmin) {
                cachedUserOptions = options
            }
            setUserOptions(options)
        } catch (e) {
            console.error('Error fetching users:', e)
            errorToast('Failed to load company users.')
        } finally {
            setLoadingUsers(false)
        }
    }

    // Load users based on selected company
    useEffect(() => {
        fetchUsers()
    }, [targetCompanyId, isSuperAdmin, selectedCompanyId])

    const handleUserCreated = () => {
        cachedUserOptions = null
        fetchUsers(true)
        refetchUsage()
    }

    const selectedUser = useMemo(() => {
        if (!selectedUserId) return null
        const option = userOptions.find(o => o.value === selectedUserId)
        return option?.user || null
    }, [selectedUserId, userOptions])

    const handleEditUser = () => {
        if (!selectedUser) return
        setEditUserModalOpen(true)
    }

    const handleEditModalClose = shouldRefresh => {
        setEditUserModalOpen(false)
        if (shouldRefresh) {
            cachedUserOptions = null
            fetchUsers(true)
        }
    }

    // Load user permissions when user is selected
    useEffect(() => {
        if (selectedUserId) {
            fetchUserPermissions(selectedUserId, true)
        } else {
            setUserPermissions([])
            setModulePermissionsMap({})
            setExpandedModules({})
        }
    }, [selectedUserId])

    const rootMenus = useMemo(() => {
        if (!menuList) return []
        const root = menuList.filter(menu => menu.ParentMenuId === null)
        return root.filter(menu => hasMenuPermission(companyPermissions, menu))
    }, [menuList, companyPermissions])

    const fetchUserPermissions = async (userId, showLoader = true) => {
        if (showLoader) {
            setLoadingPermissions(true)
        }
        try {
            const res = await PermissionSettingService.getUserPermissions(userId)
            const perms = Array.isArray(res) ? res : (res?.data || [])
            setUserPermissions(perms)
        } catch (error) {
            console.error('Error loading user permissions:', error)
            errorToast('Failed to load user permissions.')
        } finally {
            if (showLoader) {
                setLoadingPermissions(false)
            }
        }
    }

    const isModuleEnabled = menu => hasMenuPermission(userPermissions, menu)

    const getModuleStatusText = menu => {
        const isCompModuleEnabled = hasMenuPermission(companyPermissions, menu)
        if (!isCompModuleEnabled) return 'Not enabled for company'

        const details = modulePermissionsMap[menu.MenuId]
        if (!details || !details.loaded) return 'Click to load permissions'

        const total = (details.menuVisibility?.length || 0) + (details.actionsData?.length || 0)
        if (total === 0) return 'No sub-menus or actions available'

        const enabledCount = [
            ...(details.menuVisibility || []),
            ...(details.actionsData || []),
        ].filter(perm =>
            userPermissions.some(up => up.PermissionId === perm.PermissionId),
        ).length

        if (enabledCount === total) return 'All sub-menus & actions enabled'
        if (enabledCount === 0) return 'All sub-menus & actions disabled'
        return `${enabledCount} of ${total} sub-menus & actions enabled`
    }

    const handleToggleModule = async (moduleKey, isChecked) => {
        if (!selectedUserId) return

        // Find corresponding menu and its sub-permissions
        const menu = menuList?.find(m => {
            const key = m.MenuKey.startsWith('menu.') ? m.MenuKey.slice(5) : m.MenuKey
            return key === moduleKey
        })
        if (!menu) return

        const details = modulePermissionsMap[menu.MenuId]
        const subPerms = details ? [
            ...(details.menuVisibility || []),
            ...(details.actionsData || []),
        ] : []

        // Optimistically update state using menu.*.view keys
        const menuKey = menu.MenuKey || ''
        const expectedKey = `${menuKey}.view`.toLowerCase()
        setUserPermissions(prev => {
            if (isChecked) {
                const nextPerms = [...prev]
                if (!nextPerms.some(p => p.PermissionKey?.toLowerCase() === expectedKey)) {
                    nextPerms.push({ PermissionId: `temp-view-${menu.MenuId}`, PermissionKey: expectedKey })
                }
                subPerms.forEach(sp => {
                    const isCompPermEnabled = companyPermissions.some(cp => cp.PermissionId === sp.PermissionId)
                    if (isCompPermEnabled && !nextPerms.some(p => p.PermissionId === sp.PermissionId)) {
                        nextPerms.push(sp)
                    }
                })
                return nextPerms
            }
            let nextPerms = prev.filter(p => p.PermissionKey?.toLowerCase() !== expectedKey)
            const subPermIds = new Set(subPerms.map(sp => sp.PermissionId))
            nextPerms = nextPerms.filter(p => !subPermIds.has(p.PermissionId))
            return nextPerms
        })

        try {
            await PermissionSettingService.toggleUserModule(selectedUserId, moduleKey, isChecked)
            await fetchUserPermissions(selectedUserId, false)
            successToast(`Module "${menu.Title}" ${isChecked ? 'enabled' : 'disabled'} successfully.`)
        } catch (error) {
            console.error('Error toggling module:', error)
            errorToast('Failed to update module state.')
            // Rollback optimistic update
            await fetchUserPermissions(selectedUserId, false)
        }
    }

    const handleTogglePermission = async (permission, isChecked) => {
        if (!selectedUserId) return
        const permissionId = permission.PermissionId

        // Optimistically update state
        setUserPermissions(prev => {
            if (isChecked) {
                if (!prev.some(p => p.PermissionId === permissionId)) {
                    return [...prev, permission]
                }
                return prev
            }
            return prev.filter(p => p.PermissionId !== permissionId)
        })

        try {
            await PermissionSettingService.saveUserPermission(selectedUserId, permissionId, isChecked)
            await fetchUserPermissions(selectedUserId, false)
            successToast(`Permission '${permission.PermissionName}' updated successfully.`)
        } catch (error) {
            console.error('Error toggling permission:', error)
            errorToast('Failed to update permission state.')
            // Rollback optimistic update
            await fetchUserPermissions(selectedUserId, false)
        }
    }

    const loadModuleDetails = async (rootMenu) => {
        const subMenus = menuList.filter(m => m.ParentMenuId === rootMenu.MenuId)
        const allMenuIds = [rootMenu.MenuId, ...subMenus.map(m => m.MenuId)]

        setLoadingModuleId(rootMenu.MenuId)
        try {
            const mappings = await Promise.all(
                allMenuIds.map(async (menuId) => {
                    try {
                        const res = await MenuSettingService.menuPermission(menuId)
                        return { menuId, permissions: Array.isArray(res) ? res : [] }
                    } catch {
                        return { menuId, permissions: [] }
                    }
                }),
            )

            const allPerms = []
            const seenIds = new Set()
            mappings.forEach(m => {
                m.permissions.forEach(p => {
                    if (!seenIds.has(p.PermissionId)) {
                        seenIds.add(p.PermissionId)
                        allPerms.push(p)
                    }
                })
            })

            const rootMenuViewKey = `${rootMenu.MenuKey}.view`.toLowerCase()
            const menuVisPerms = allPerms.filter(p => {
                const key = (p.PermissionKey || '').toLowerCase()
                return (
                    key.startsWith('menu.') &&
                    key !== rootMenuViewKey &&
                    companyPermissions.some(cp => cp.PermissionId === p.PermissionId)
                )
            })

            const actionsDataPerms = allPerms.filter(p => {
                const key = (p.PermissionKey || '').toLowerCase()
                return !key.startsWith('menu.') && companyPermissions.some(cp => cp.PermissionId === p.PermissionId)
            })

            setModulePermissionsMap(prev => ({
                ...prev,
                [rootMenu.MenuId]: {
                    menuVisibility: menuVisPerms,
                    actionsData: actionsDataPerms,
                    loaded: true,
                },
            }))
        } catch (error) {
            console.error('Error loading module details:', error)
            errorToast('Failed to load module details.')
        } finally {
            setLoadingModuleId(null)
        }
    }

    const toggleExpand = (menu) => {
        const menuId = menu.MenuId
        const isExpanded = !expandedModules[menuId]
        setExpandedModules(prev => ({ ...prev, [menuId]: isExpanded }))

        const details = modulePermissionsMap[menuId]
        if (isExpanded && (!details || !details.loaded)) {
            loadModuleDetails(menu)
        }
    }
    const impersonationTarget = useMemo(() => {
        if (!selectedUserId) return null
        const option = userOptions.find(o => o.value === selectedUserId)
        const company = companyOptions.find(c => c.value === selectedCompanyId)
        return {
            userId: selectedUserId,
            name: option?.name || selectedUser?.Name || selectedUser?.FullName,
            email: option?.email || selectedUser?.Email,
            companyName: company?.text || currentUser?.CompanyName || '',
        }
    }, [selectedUserId, userOptions, companyOptions, selectedCompanyId, currentUser, selectedUser])

    const formatLastLogin = dateTimeString => {
        if (!dateTimeString) return '—'
        try {
            return new Date(dateTimeString).toLocaleString()
        } catch {
            return dateTimeString
        }
    }

    const renderModuleList = () => {
        if (loadingPermissions || loadingMenuList || loadingCompanyPermissions) {
            return <LoaderBar content="Loading modules and permission states..." />
        }
        if (rootMenus.length === 0) {
            return (
                <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', fontSize: '14px', fontWeight: '500' }}>
                    No modules are assigned to this company.
                </div>
            )
        }
        return rootMenus.map(menu => {
            const moduleKey = menu.MenuKey.startsWith('menu.') ? menu.MenuKey.slice(5) : menu.MenuKey
            const description = MODULE_DESCRIPTIONS[menu.MenuKey] || `Manage settings and visibility for the ${menu.Title} module.`
            const isEnabled = isModuleEnabled(menu)
            const hasSubMenus = menuList ? menuList.some(m => m.ParentMenuId === menu.MenuId) : false
            const isExpandable = isEnabled && hasSubMenus
            const isExpanded = isExpandable && !!expandedModules[menu.MenuId]
            const details = modulePermissionsMap[menu.MenuId]
            const isCompModuleEnabled = hasMenuPermission(companyPermissions, menu)

            let caretElement = <div style={{ width: '30px' }} />
            if (isExpandable) {
                caretElement = isExpanded ? (
                    <CaretDown size={18} style={{ marginRight: '12px', color: '#6b7280' }} />
                ) : (
                    <CaretRight size={18} style={{ marginRight: '12px', color: '#6b7280' }} />
                )
            }

            return (
                <ModuleRowContainer key={menu.MenuId} style={{ opacity: isCompModuleEnabled ? 1 : 0.55 }}>
                    <ModuleHeaderRow
                        onClick={() => isCompModuleEnabled && isExpandable && toggleExpand(menu)}
                        style={{ cursor: isCompModuleEnabled && isExpandable ? 'pointer' : 'default' }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {caretElement}
                            <IconContainer>{getMenuIcon(menu.IconKey, menu.Title)}</IconContainer>
                            <div>
                                <div style={{ fontWeight: '600', fontSize: '15px', color: '#111827', marginBottom: '2px' }}>
                                    {menu.Title}
                                </div>
                                <div style={{ fontSize: '12.5px', color: '#6b7280' }}>
                                    {description}
                                </div>
                                <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px', fontWeight: '500' }}>
                                    {getModuleStatusText(menu)}
                                </div>
                            </div>
                        </div>
                        <div onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                                toggle
                                checked={isEnabled}
                                onChange={(e, data) => handleToggleModule(moduleKey, data.checked)}
                                disabled={!isCompModuleEnabled}
                            />
                        </div>
                    </ModuleHeaderRow>

                    {isExpanded && (
                        <div style={{ borderTop: '1px solid #e5e7eb', backgroundColor: '#fafafa' }}>
                            {loadingModuleId === menu.MenuId ? (
                                <div style={{ padding: '16px' }}>
                                    <LoaderBar content="Loading sub-menus and permissions..." />
                                </div>
                            ) : (
                                <>
                                    {(!details?.menuVisibility?.length && !details?.actionsData?.length) ? (
                                        <div style={{ padding: '16px', color: '#6b7280', fontSize: '13px' }}>
                                            No sub-menus or actions available for this module.
                                        </div>
                                    ) : (
                                        <>
                                            {details.menuVisibility?.length > 0 && (
                                                <>
                                                    <SubHeader>Menu Visibility</SubHeader>
                                                    {details.menuVisibility.map(menuItem => {
                                                        const isCompPermEnabled = companyPermissions.some(cp => cp.PermissionId === menuItem.PermissionId)
                                                        const isPermEnabled = userPermissions.some(up => up.PermissionId === menuItem.PermissionId)

                                                        return (
                                                            <SubRow key={menuItem.PermissionId} style={{ opacity: isCompPermEnabled ? 1 : 0.55 }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                    <div style={{ fontWeight: '500', fontSize: '13.5px', color: '#374151' }}>
                                                                        {menuItem.PermissionName}
                                                                    </div>
                                                                    {!isCompPermEnabled && (
                                                                        <span style={{ fontSize: '11px', color: '#ef4444', backgroundColor: '#fee2e2', padding: '2px 6px', borderRadius: '4px', fontWeight: '500' }}>
                                                                            Not enabled for company
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <Checkbox
                                                                    toggle
                                                                    checked={isPermEnabled}
                                                                    onChange={(e, data) => handleTogglePermission(menuItem, data.checked)}
                                                                    disabled={!isEnabled || !isCompPermEnabled}
                                                                />
                                                            </SubRow>
                                                        )
                                                    })}
                                                </>
                                            )}

                                            {details.actionsData?.length > 0 && (
                                                <>
                                                    <SubHeader>Actions & Data Access</SubHeader>
                                                    {details.actionsData.map(perm => {
                                                        const isCompPermEnabled = companyPermissions.some(cp => cp.PermissionId === perm.PermissionId)
                                                        const isPermEnabled = userPermissions.some(up => up.PermissionId === perm.PermissionId)

                                                        return (
                                                            <SubRow key={perm.PermissionId} style={{ opacity: isCompPermEnabled ? 1 : 0.55 }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                    <div style={{ fontWeight: '500', fontSize: '13.5px', color: '#374151' }}>
                                                                        {perm.PermissionName}
                                                                    </div>
                                                                    {!isCompPermEnabled && (
                                                                        <span style={{ fontSize: '11px', color: '#ef4444', backgroundColor: '#fee2e2', padding: '2px 6px', borderRadius: '4px', fontWeight: '500' }}>
                                                                            Not enabled for company
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <Checkbox
                                                                    toggle
                                                                    checked={isPermEnabled}
                                                                    onChange={(e, data) => handleTogglePermission(perm, data.checked)}
                                                                    disabled={!isEnabled || !isCompPermEnabled}
                                                                />
                                                            </SubRow>
                                                        )
                                                    })}
                                                </>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </ModuleRowContainer>
            )
        })
    }

    return (
        <Container>
            <HeaderRow>
                <PageHeader header="Company Admin: User-level Permissions" />
                {canAddUser ? (
                    <AppPrimaryButton
                        type="button"
                        onClick={() => setCreateUserModalOpen(true)}
                        disabled={isSuperAdmin && !selectedCompanyId}
                    >
                        + Add User
                    </AppPrimaryButton>
                ) : null}
            </HeaderRow>

            {isFreePlan && <PlanUsageStrip metric="seats" />}

            <DropdownGrid>
                {isSuperAdmin && (
                    <div>
                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '13.5px', color: '#374151' }}>
                            Pick a Company
                        </label>
                        <Dropdown
                            placeholder="Select Company"
                            fluid
                            search
                            selection
                            selectOnNavigation={false}
                            selectOnBlur={false}
                            options={companyOptions}
                            value={selectedCompanyId}
                            onChange={(e, { value }) => setSelectedCompanyId(value)}
                            onSearchChange={(e, { searchQuery }) => setCompanySearchText(searchQuery)}
                            loading={loadingCompanies}
                        />
                    </div>
                )}
                <div>
                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '13.5px', color: '#374151' }}>
                        Pick a User
                    </label>
                    <Dropdown
                        placeholder={loadingUsers ? "Loading users..." : "Select User"}
                        fluid
                        selection
                        search
                        selectOnNavigation={false}
                        selectOnBlur={false}
                        options={userOptions}
                        value={selectedUserId}
                        onChange={(e, { value }) => setSelectedUserId(value)}
                        loading={loadingUsers}
                        disabled={loadingUsers || (isSuperAdmin && !selectedCompanyId)}
                    />
                </div>
            </DropdownGrid>

            {selectedUser && (
                <UserDetailCard>
                    <UserDetailGrid>
                        <DetailItem>
                            <div className="label">Name</div>
                            <div className="value">
                                {selectedUser.Name || selectedUser.FullName || '—'}
                            </div>
                        </DetailItem>
                        <DetailItem>
                            <div className="label">Email</div>
                            <div className="value">{selectedUser.Email || '—'}</div>
                        </DetailItem>
                        <DetailItem>
                            <div className="label">Phone</div>
                            <div className="value">{selectedUser.PhoneNo || '—'}</div>
                        </DetailItem>
                        <DetailItem>
                            <div className="label">User ID</div>
                            <div className="value">
                                {selectedUser.UserId || selectedUser.unique_id || '—'}
                            </div>
                        </DetailItem>
                        <DetailItem>
                            <div className="label">Role</div>
                            <div className="value">
                                {selectedUser.Role || selectedUser.RoleName || '—'}
                            </div>
                        </DetailItem>
                        <DetailItem>
                            <div className="label">Status</div>
                            <div className="value">
                                <StatusDot active={selectedUser.IsActive !== false} />
                                {selectedUser.IsActive === false ? 'Inactive' : 'Active'}
                            </div>
                        </DetailItem>
                        <DetailItem>
                            <div className="label">Last Login</div>
                            <div className="value">
                                {formatLastLogin(selectedUser.LastLoginDateTime)}
                            </div>
                        </DetailItem>
                    </UserDetailGrid>
                    <AppEditButton onClick={handleEditUser} />
                </UserDetailCard>
            )}

            {isSuperAdmin && selectedUserId && (
                <ImpersonateStrip>
                    <UserSwitch size={22} weight="bold" style={{ color: '#166534' }} />
                    <div className="label">
                        <strong>Super Admin only:</strong> Log in as this user to
                        reproduce a bug they&apos;re reporting. Your actions will
                        be logged under an impersonation session; destructive
                        endpoints are blocked while impersonating.
                    </div>
                    <button
                        type="button"
                        className="ui primary button"
                        style={{ whiteSpace: 'nowrap' }}
                        onClick={() => setImpersonateModalOpen(true)}
                    >
                        Login as this user
                    </button>
                </ImpersonateStrip>
            )}

            <LoginAsUserModal
                open={impersonateModalOpen}
                onClose={() => setImpersonateModalOpen(false)}
                target={impersonationTarget}
            />

            <CreateUserModal
                open={createUserModalOpen}
                onClose={() => setCreateUserModalOpen(false)}
                onCreated={handleUserCreated}
                companyId={targetCompanyId}
            />

            <UserEditModal
                open={editUserModalOpen}
                onClose={handleEditModalClose}
                user={selectedUser}
            />

            {selectedUserId && (
                <SectionCard>
                    <CardHeader>
                        <Package size={18} style={{ color: '#4b5563' }} />
                        Modules available for this user
                    </CardHeader>
                    <CardBody>
                        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                            Toggle a module on/off for this user. Changes take effect on the user's next login.
                        </div>
                        {renderModuleList()}
                    </CardBody>
                </SectionCard>
            )}
        </Container>
    )
}
